<template>
    <div ref="box" class="bar-chart" :style="{ height: `${height}px` }" @mouseleave="hover = null">
        <svg v-if="width" :width="width" :height="height" class="bar-chart__svg">
            <g v-for="tick in ticks" :key="`t${tick}`">
                <line :x1="padLeft" :x2="width - padRight" :y1="y(tick)" :y2="y(tick)" class="bar-chart__grid" />
                <text :x="padLeft - 8" :y="y(tick) + 4" text-anchor="end" class="bar-chart__axis">{{ formatTick(tick) }}</text>
            </g>
            <g v-for="(label, index) in labels" :key="`c${index}`">
                <rect
                    :x="slotX(index)"
                    :y="padTop"
                    :width="slotWidth"
                    :height="plotHeight"
                    :class="['bar-chart__slot', { 'bar-chart__slot--active': hover === index }]"
                    @mouseenter="hover = index"
                    @click="hover = index"
                />
                <rect
                    v-for="bar in barsAt(index)"
                    :key="bar.key"
                    :x="bar.x"
                    :y="bar.y"
                    :width="bar.width"
                    :height="bar.height"
                    :fill="bar.color"
                    rx="2"
                    class="bar-chart__bar"
                    @mouseenter="hover = index"
                />
                <text
                    v-if="index % labelStep === 0"
                    :x="slotX(index) + slotWidth / 2"
                    :y="height - 8"
                    text-anchor="middle"
                    class="bar-chart__axis"
                >
                    {{ label }}
                </text>
            </g>
        </svg>
        <div v-if="hover !== null && width" class="bar-chart__tip" :style="tipStyle">
            <div class="bar-chart__tip-title">
                {{ tooltipTitles[hover] || labels[hover] }}
            </div>
            <div v-for="item in series" :key="item.label" class="bar-chart__tip-row">
                <span class="bar-chart__dot" :style="{ background: item.color }" />{{ item.label }}
                <b>{{ formatValue(item.values[hover] || 0) }}</b>
            </div>
            <div v-for="extra in (tooltipExtra[hover] || [])" :key="extra.label" class="bar-chart__tip-row bar-chart__tip-row--extra">
                {{ extra.label }}
                <b>{{ extra.value }}</b>
            </div>
        </div>
        <div v-if="!labels.length" class="bar-chart__empty">
            No data for this period
        </div>
    </div>
</template>

<script>
    // Small dependency-free bar chart: one slot per label, series side by side or stacked.
    export default {
        name: 'BarChart',
        props: {
            labels: {
                type: Array,
                default: () => []
            },
            // [{ label, color, values: [] }] (values aligned with labels)
            series: {
                type: Array,
                default: () => []
            },
            stacked: {
                type: Boolean,
                default: false
            },
            height: {
                type: Number,
                default: 260
            },
            formatValue: {
                type: Function,
                default: value => String(value)
            },
            // per label: longer title for the tooltip and extra rows [{ label, value }]
            tooltipTitles: {
                type: Array,
                default: () => []
            },
            tooltipExtra: {
                type: Array,
                default: () => []
            }
        },
        data: () => ({
            width: 0,
            hover: null,
            padTop: 12,
            padRight: 8,
            padBottom: 28
        }),
        computed: {
            padLeft() {
                return Math.max(36, this.formatTick(this.niceMax).length * 7 + 14)
            },
            plotHeight() {
                return this.height - this.padTop - this.padBottom
            },
            slotWidth() {
                return this.labels.length ? (this.width - this.padLeft - this.padRight) / this.labels.length : 0
            },
            maxValue() {
                let max = 0
                this.labels.forEach((label, index) => {
                    const values = this.series.map(item => Math.max(0, item.values[index] || 0))
                    max = Math.max(max, this.stacked ? values.reduce((sum, value) => sum + value, 0) : Math.max(0, ...values))
                })
                return max
            },
            // 1/2/5 x 10^n step, 4 grid lines
            niceMax() {
                if (this.maxValue <= 0) return 4
                const rough = this.maxValue / 4
                const power = Math.pow(10, Math.floor(Math.log10(rough)))
                const step = [1, 2, 5, 10].map(m => m * power).find(candidate => candidate >= rough)
                return Math.max(step * 4, this.maxValue <= 4 ? 4 : 0)
            },
            ticks() {
                return [0, 1, 2, 3, 4].map(i => (this.niceMax / 4) * i)
            },
            // skip x labels so they never overlap (about 56px each)
            labelStep() {
                return Math.max(1, Math.ceil(56 / Math.max(1, this.slotWidth)))
            },
            tipStyle() {
                const center = this.slotX(this.hover) + this.slotWidth / 2
                const left = Math.min(Math.max(center, 90), this.width - 90)
                return { left: `${left}px`, top: `${this.padTop}px` }
            }
        },
        mounted() {
            this.measure()
            if (window.ResizeObserver) {
                this.observer = new ResizeObserver(() => this.measure())
                this.observer.observe(this.$refs.box)
            } else {
                window.addEventListener('resize', this.measure)
            }
        },
        beforeDestroy() {
            if (this.observer) this.observer.disconnect()
            window.removeEventListener('resize', this.measure)
        },
        methods: {
            measure() {
                if (this.$refs.box) this.width = this.$refs.box.clientWidth
            },
            y(value) {
                return this.padTop + this.plotHeight - (value / this.niceMax) * this.plotHeight
            },
            slotX(index) {
                return this.padLeft + index * this.slotWidth
            },
            formatTick(value) {
                return Math.abs(value) >= 1000 ? `${Math.round(value / 100) / 10}k` : String(Math.round(value * 10) / 10)
            },
            barsAt(index) {
                const inner = this.slotWidth * 0.72
                const left = this.slotX(index) + (this.slotWidth - inner) / 2
                if (this.stacked) {
                    let base = 0
                    return this.series.map(item => {
                        const value = Math.max(0, item.values[index] || 0)
                        const bar = { key: item.label, x: left, width: inner, color: item.color, y: this.y(base + value), height: this.y(base) - this.y(base + value) }
                        base += value
                        return bar
                    }).filter(bar => bar.height > 0)
                }
                const each = inner / Math.max(1, this.series.length)
                return this.series.map((item, position) => {
                    const value = Math.max(0, item.values[index] || 0)
                    return { key: item.label, x: left + position * each + 1, width: Math.max(1, each - 2), color: item.color, y: this.y(value), height: this.y(0) - this.y(value) }
                }).filter(bar => bar.height > 0)
            }
        }
    }
</script>

<style scoped>
.bar-chart {
    position: relative;
    width: 100%;
}

.bar-chart__svg {
    display: block;
}

.bar-chart__grid {
    stroke: rgba(0, 0, 0, 0.08);
}

.bar-chart__axis {
    font-size: 11px;
    fill: rgba(0, 0, 0, 0.55);
}

.bar-chart__slot {
    fill: transparent;
    cursor: pointer;
}

.bar-chart__slot--active {
    fill: rgba(0, 150, 136, 0.08);
}

.bar-chart__bar {
    pointer-events: auto;
    cursor: pointer;
}

.bar-chart__tip {
    position: absolute;
    transform: translateX(-50%);
    min-width: 160px;
    padding: 8px 10px;
    border-radius: 8px;
    background: rgba(33, 33, 33, 0.94);
    color: #fff;
    font-size: 12px;
    pointer-events: none;
    z-index: 2;
}

.bar-chart__tip-title {
    font-weight: 700;
    margin-bottom: 4px;
}

.bar-chart__tip-row {
    display: flex;
    align-items: center;
    gap: 6px;
}

.bar-chart__tip-row b {
    margin-left: auto;
    padding-left: 12px;
}

.bar-chart__tip-row--extra {
    margin-top: 2px;
    padding-top: 2px;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.bar-chart__dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex: none;
}

.bar-chart__empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
}
</style>
